import { useState } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { FIELD_KEYS } from "@shared/constants/fields";
import { formatAddressSummary } from "@shared/address";
import { OPTION_VALUE } from "@shared/interview/board-map";
import { useSectionRows, type SectionRow } from "../useSectionRows";
import { PRIMARY_RESIDENCE } from "./Ch2Engine";
import { isRecordRow } from "./ch2Bindings";

/**
 * s237 HOME-REDESIGN-1 PR 3 (D-236.47, D-236.88-1, -6, -8; rule 159: one job, one piece) — THE planner's home, for both
 * chapters. Chapter one asks "Is this home…" under the home address (YouScreen); chapter two's "Where you live" tiles ask
 * the same answer until PR 4 removes them. Both write About You's home_tenure (stored on the "you" person's
 * persons.home_tenure, PR #214) and both create or flag the home row through this ONE hook — moved here from Ch2Screens,
 * never forked:
 *  • own → the owned-property table's row flagged where the planner lives (is_residence), kind Primary Residence;
 *  • rent → the rental table's row flagged so, kind A flat or house; a new one starts at the home address (rule 42);
 *  • family → no home row (D-236.47(3), D-237.41); the family answers start their own row on the first one.
 * Creating a home is Class A (a second fire is a second home): ONE single-flight lock for either kind. The About You
 * writer is passed in (`useAboutYou`, Ch2Screens — its mutation stays registered there).
 * No answer is a default (D-236.88-8): unanswered stays unanswered.
 */

/** The home's answer, as About You's home_tenure cell stores it (template-spec about-you.ts; the server's
 *  HOME_TENURE_LABELS maps the same three words to persons.home_tenure's own / rent / family). */
export const TENURE_CELL = { own: "Own", rent: "Rent", family: "Neither (I live with family / other)" } as const;
export type Tenure = keyof typeof TENURE_CELL;

/** The stored cell → the answer; blank or a word off the list is unanswered (never guessed). Pure. */
export function tenureOf(cell: string | undefined): Tenure | null {
  return (Object.entries(TENURE_CELL).find(([, v]) => v === cell)?.[0] as Tenure | undefined) ?? null;
}

/** s235 PR B (D-235.65): a rented home starts as "A flat or house" — the board's word, stored as the rent table stores it. */
const RENTED_HOME_KIND = OPTION_VALUE.prop["rent.kind"]["A flat or house"];
/** s235 PR B (D-235.65): the old home's line — its own address, or "this place" when it has none. Pure. */
export const stillQuestion = (verb: "own" | "rent", address: string) => `Do you still ${verb} ${formatAddressSummary(address).trim() || "this place"}?`;

export type HomeTable = "own" | "rent";

/** s237 (D-237.41; Reade's s237 ruling "by its kind"): the "with family" answers live on a rental row flagged as the home,
 *  started by the first family answer (never by the pick). That row is told from an old rented home by its kind: one of
 *  the two the family question writes (board-map `fam.care`: Care Residence / Other) is the family row; a rented home
 *  starts as A flat or house. A BLANK kind belongs to whichever of the two the answer is (an older rented home has no
 *  kind; a family row has none until "Is it a care home?" is answered). Pure. */
const FAMILY_KINDS: readonly string[] = Object.values(OPTION_VALUE.prop["fam.care"]);
export const isFamilyRow = (r: SectionRow, tenure: Tenure | null): boolean => {
  const kind = (r.values[FIELD_KEYS.HOUSING_TYPE] ?? "").trim();
  return FAMILY_KINDS.includes(kind) || (kind === "" && tenure === "family");
};
/** About You's card as `useAboutYou` (Ch2Screens) returns it — the one About You writer both chapters pass in. */
type About = { ready: boolean; values: Readonly<Record<string, string>>; write: (key: string, value: string) => Promise<void> };

export function useHome(clientId: string, onAnswered: () => void, about: About) {
  const props = useSectionRows(clientId, CATEGORY_SLUGS.ASSETS, SECTION_SLUGS.ASSETS_PROPERTIES, onAnswered, { typed: true });
  const rentals = useSectionRows(clientId, CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE, SECTION_SLUGS.FINANCIAL_AND_INSURANCE_HOUSING, onAnswered, { typed: true });
  const createFlight = useSingleFlight();
  const tenure = tenureOf(about.values[FIELD_KEYS.HOME_TENURE]);
  // s234 (CH2-STORAGE-PR3, D-233.70(2)): the owned home is the row flagged as where the planner lives (is_residence), as
  // the rented one is — its kind then changes freely.
  const flagged = (r: SectionRow) => isRecordRow(r.rowId) && !r.hidden && r.values[FIELD_KEYS.IS_RESIDENCE] === "true";
  const own = props.rows.find(flagged);
  /** The rented home, and (separately) the row the family answers live on — never the same row (D-237.41). */
  const home = rentals.rows.find((r) => flagged(r) && !isFamilyRow(r, tenure));
  const famRow = rentals.rows.find((r) => flagged(r) && isFamilyRow(r, tenure));
  const [started, setStarted] = useState<string | null>(null);
  // s232 walk (ruling 4): a plan can hold the answer with NO row behind it (answered on the classic page, or a refused
  // create). Its questions still show, on a row created by its first answer.
  const [pendingId] = useState(() => crypto.randomUUID());
  /** What a new home row starts with: the home flag, its kind, and the chapter-one address (or the one being saved). */
  const seedFor = (t: Tenure, addressNow?: string): Record<string, string> => {
    const address = addressNow ?? about.values[FIELD_KEYS.HOME_ADDRESS] ?? "";
    return t === "own"
      ? { [FIELD_KEYS.IS_RESIDENCE]: "true", [FIELD_KEYS.PROP_TYPE]: PRIMARY_RESIDENCE.value, ...(address ? { [FIELD_KEYS.ADDRESS]: address } : {}) }
      : { [FIELD_KEYS.IS_RESIDENCE]: "true", ...(t === "rent" ? { [FIELD_KEYS.HOUSING_TYPE]: RENTED_HOME_KIND } : {}), ...(address ? { [FIELD_KEYS.RENTAL_ADDRESS]: address } : {}) };
  };

  /** Answer the home (null clears it), then create the home row its answer needs, once. With family creates none
   *  (D-236.47(3), D-237.41): its answers start their own row on the first one. */
  const pick = async (next: Tenure | null) => {
    await about.write(FIELD_KEYS.HOME_TENURE, next ? TENURE_CELL[next] : "");
    if (!next) return;
    // Class A: a second fire would be a second home — ONE lock for starting either kind.
    if (next === "own" && !own) {
      const rowId = await createFlight.run(() => props.createRow(seedFor("own"), pendingId));
      if (rowId) setStarted(rowId);
    }
    if (next === "rent" && !home) {
      const rowId = await createFlight.run(() => rentals.createRow(seedFor(next), pendingId));
      if (rowId) setStarted(rowId);
    }
  };

  /** s235 PR B (D-235.65): a home row that is no longer where the planner lives asks one thing — Keep clears its home
   *  flag (the row stays, an ordinary place); Remove is the row's own delete (it hides the row, G8).
   *  s237 D-237.40: it asks only once the answer has MOVED away from the row — never while unanswered. D-237.41: Rented →
   *  With family asks too. One rule: a home row asks unless it matches the answer (the owned home ↔ Owned, a rented home ↔
   *  Rented, the family answers' row ↔ With family). */
  const stillHere = (table: HomeTable, r: SectionRow): { question: string; keep: Record<string, string> } | undefined => {
    if (r.values[FIELD_KEYS.IS_RESIDENCE] !== "true" || tenure === null) return undefined;
    const matches = table === "own" ? tenure === "own" : isFamilyRow(r, tenure) ? tenure === "family" : tenure === "rent";
    if (matches) return undefined;
    return table === "own"
      ? { question: stillQuestion("own", r.values[FIELD_KEYS.ADDRESS] ?? ""), keep: { [FIELD_KEYS.IS_RESIDENCE]: "false" } }
      : { question: stillQuestion("rent", r.values[FIELD_KEYS.RENTAL_ADDRESS] ?? ""), keep: { [FIELD_KEYS.IS_RESIDENCE]: "false" } };
  };
  /** The old homes that ask, each with its table's rows surface (chapter one lists them under the question). */
  const stale = [
    ...props.rows.filter(flagged).map((r) => ({ table: "own" as const, data: props, row: r })),
    ...rentals.rows.filter(flagged).map((r) => ({ table: "rent" as const, data: rentals, row: r })),
  ].flatMap((x) => { const s = stillHere(x.table, x.row); return s ? [{ ...x, ...s }] : []; });
  const keep = (x: (typeof stale)[number]) => x.data.writeCells(x.row.rowId, x.keep);
  const remove = async (x: (typeof stale)[number]) => { await x.data.setRowHidden(x.row.rowId, true); x.data.refresh(); };

  /**
   * s237 PR 5 (part C, D-235.65 on an address change): Personal details' address is changing while the home row holds a
   * different one — "Do you still own / rent <old address>?". Null when there is nothing to ask (no home row, an empty or
   * equal address, With family / no answer). Pure over the hook's rows.
   */
  const homeRow = tenure === "own" ? own : tenure === "rent" ? home : undefined;
  const addressChange = (next: string): { question: string; rowId: string } | null => {
    if (!homeRow || (tenure !== "own" && tenure !== "rent")) return null;
    const old = homeRow.values[tenure === "own" ? FIELD_KEYS.ADDRESS : FIELD_KEYS.RENTAL_ADDRESS] ?? "";
    if (!formatAddressSummary(old).trim() || formatAddressSummary(old) === formatAddressSummary(next)) return null;
    return { question: stillQuestion(tenure, old), rowId: homeRow.rowId };
  };
  /** The answer to that question, then the new address: Keep makes the old row an ordinary place (its flag cleared FIRST,
   *  so the one address writer never moves it); Remove is the row's own delete (hidden, G8). Then About You takes the new
   *  address and the home starts again there — once (Class A). */
  const moveHome = async (next: string, choice: "keep" | "remove") => {
    if (homeRow && (tenure === "own" || tenure === "rent")) {
      const data = tenure === "own" ? props : rentals;
      if (choice === "keep") await data.writeCells(homeRow.rowId, { [FIELD_KEYS.IS_RESIDENCE]: "false" });
      else { await data.setRowHidden(homeRow.rowId, true); data.refresh(); }
    }
    await about.write(FIELD_KEYS.HOME_ADDRESS, next);
    if (tenure === "own") await createFlight.run(() => props.createRow(seedFor("own", next)));
    if (tenure === "rent") await createFlight.run(() => rentals.createRow(seedFor("rent", next)));
  };

  return { ready: about.ready && props.ready && rentals.ready, props, rentals, tenure, own, home, famRow, started, pendingId, seedFor, pick, stillHere, stale, keep, remove, addressChange, moveHome };
}
